/**
 * SCRUM-236 (DS-015 §13.8) — stable react-intl message-id constants for the
 * entity-change / Activity rendering vocabulary.
 *
 * These are message IDENTIFIERS, not user-facing copy — the copy lives in the
 * locale catalog ({@link ./messages/en.ts}). Field labels, enum-value labels,
 * and backend `semanticLabelKey` values are data-driven (looked up by computed
 * id), so they are NOT enumerated here; this module only names the fixed
 * presentational vocabulary the formatters / component reference directly.
 */

/** Fixed change-rendering vocabulary (DS-015 §6/§13.8). */
export const HISTORY_MESSAGE_IDS = {
  /** Neutral marker for an undecodable / out-of-contract value — never leaks raw data. */
  valueUnknown: 'history.value.unknown',
  /** Marker for a complex/structured value we display generically (e.g. bed config). */
  valueComplex: 'history.value.complex',
  /** Empty / cleared value (deleted side, or null on a mapped field). */
  valueCleared: 'history.value.cleared',
  /** Boolean true / false display. */
  booleanTrue: 'history.value.boolean.true',
  booleanFalse: 'history.value.boolean.false',
  /** `{from} → {to}` connector for an updated field. */
  changeConnector: 'history.change.connector',
  /** Redacted field — presence only, no values (DS-015 §5). Also the neutral
   * "changed" detail for an unmapped / leaked field (the field name is the label). */
  changeRedacted: 'history.change.redacted',
  /** Generic overlay fallback when a `semanticLabelKey` has no catalog entry. */
  semanticCreated: 'history.semantic.created',
  semanticUpdated: 'history.semantic.updated',
  semanticDeleted: 'history.semantic.deleted',
} as const;

export type HistoryMessageId = (typeof HISTORY_MESSAGE_IDS)[keyof typeof HISTORY_MESSAGE_IDS];

/** Build the message id for a field label, e.g. `property.field.unitName`. */
export function fieldLabelId(entityType: string, field: string): string {
  return `entityChange.field.${entityType}.${field}`;
}

/** Build the message id for an enum-value label, e.g. `property.enum.unitCategory.home`. */
export function enumValueId(entityType: string, field: string, value: string): string {
  return `entityChange.enum.${entityType}.${field}.${value}`;
}

/**
 * Build the message id for a semantic-label overlay, e.g.
 * `entityChange.semantic.Reservation.confirmed` (SCRUM-338 A.1). This is the
 * SHARED convention: the backend capture spec emits the IDENTICAL literal key
 * (see `reservation-entity-change.capture-spec.ts`), and the FE renders it
 * verbatim as a react-intl message id. Keep both sides in lock-step.
 */
export function semanticLabelId(entityType: string, verb: string): string {
  return `entityChange.semantic.${entityType}.${verb}`;
}
